/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Autocomplete,
    Alert,
    Stack,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Backdrop,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";
import * as XLSX from "xlsx/dist/xlsx.full.min.js";
import { saveAs } from "file-saver";

const ROW_STATUS = {
    SAVED: "saved",
    MODIFIED: "modified",
    IMPORTED: "imported",
    ERROR: "error",
};

let hasLoadedOnce = false;

function TaxCodeMapping() {
    const [items, setItems] = useState([]);
    const [taxCodes, setTaxCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "", source: "" });
    const [error, setError] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importSummary, setImportSummary] = useState(null);
    const [rowStatuses, setRowStatuses] = useState({});
    const [orphanErrors, setOrphanErrors] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [errorDetail, setErrorDetail] = useState(null);
    const fileInputRef = useRef(null);

    const rilletUserId = sessionStorage.getItem("rilletUserId");

    const safeJson = async (res) => {
        const text = await res.text();
        if (!text) return [];
        try {
            return JSON.parse(text);
        } catch {
            return [];
        }
    };

    const fetchData = async (refreshZamp = false) => {
        if (!rilletUserId) {
            setError("Missing user information. Please log in again.");
            setItems([]);
            setTaxCodes([]);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const authHeaders = { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` };

            const [itemsRes, taxCodesRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/products/export/${rilletUserId}`, { headers: authHeaders }),
                fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/taxcode/zamp?tenantId=${rilletUserId}&refresh=${refreshZamp}`, { headers: authHeaders }),
            ]);

            if (itemsRes.status === 401 || taxCodesRes.status === 401) {
                handleSessionExpiredClose();
                setItems([]);
                setTaxCodes([]);
                return;
            }

            if (!itemsRes.ok || !taxCodesRes.ok) {
                const failedRes = !itemsRes.ok ? itemsRes : taxCodesRes;
                const errorBody = await safeJson(failedRes);
                throw new Error(errorBody?.message || "Something went wrong. Please try again later.");
            }

            const [itemsData, taxCodesData] = await Promise.all([safeJson(itemsRes), safeJson(taxCodesRes)]);
            setItems(itemsData || []);
            setTaxCodes(taxCodesData || []);

            const validCodes = new Set((taxCodesData || []).map(tc => tc.code));
            const deletedStatuses = {};
            (itemsData || []).forEach(item => {
                if (item.zampTaxCode && !validCodes.has(item.zampTaxCode)) {
                    deletedStatuses[item.id] = {
                        status: ROW_STATUS.ERROR,
                        tooltip: `Tax code "${item.zampTaxCode}" no longer exists in Zamp`,
                        error: `Tax code "${item.zampTaxCode}" has been deleted from Zamp`,
                        rowData: { itemName: item.name, description: item.description, taxCode: item.zampTaxCode },
                    };
                }
            });
            setRowStatuses(deletedStatuses);
            setError("");
        } catch (err) {
            setError(err.message || "Error fetching data");
            setItems([]);
            setTaxCodes([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        hasLoadedOnce = true;
        fetchData();
    }, [rilletUserId]);

    const handleTaxCodeChange = (productId, newCode) => {
        setMessage({ type: "", text: "", source: "" });
        setItems(prev => prev.map(it => {
            if (it.id !== productId) return it;
            return { ...it, zampTaxCode: newCode };
        }));
        setRowStatuses(prev => ({
            ...prev,
            [productId]: { status: ROW_STATUS.MODIFIED, tooltip: "Modified — not yet saved" },
        }));
        setUnsavedChanges(true);
    };

    const handleSave = async () => {
        if (!rilletUserId) {
            setError("Missing user information. Please log in again.");
            return;
        }
        setSaving(true);
        setImportSummary(null);
        setOrphanErrors([]);
        setStatusFilter("all");
        setMessage({ type: "", text: "", source: "" });
        try {
            const updates = items.map(item => ({
                id: item.id,
                zampTaxCode: item.zampTaxCode || null,
            }));
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/products/pscode-assignments/${rilletUserId}`,
                {
                    method: "POST",
                    headers: { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ updates }),
                }
            );
            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) throw new Error("Failed to save mappings");

            setMessage({ type: "success", text: "Tax code mappings saved successfully!", source: "save" });
            setUnsavedChanges(false);
            setRowStatuses({});
        } catch (err) {
            setMessage({ type: "error", text: err.message || "Error saving mappings", source: "save" });
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = async () => {
        setMessage({ type: "", text: "", source: "" });
        setRefreshing(true);
        try {
            const authHeaders = { ...apiHeaders, Authorization: `${sessionStorage.getItem("token")}` };
            await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/products/sync/${rilletUserId}`, { headers: authHeaders });
        } catch {
            // product sync failure is non-blocking
        }
        fetchData(true);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExport = () => {
        const exportData = items.map(it => ({
            "Product ID": it.id || "",
            "Product Name": it.name || "",
            "Description": it.description || "",
            "Zamp Tax Code": it.zampTaxCode || "",
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws["!cols"] = [{ wch: 40 }, { wch: 35 }, { wch: 40 }, { wch: 30 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "PTC Mappings");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([buf], { type: "application/octet-stream" });
        saveAs(blob, `PTC_Mappings_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setImportProgress(0);
        setMessage({ type: "", text: "", source: "" });
        setImportSummary(null);
        setRowStatuses({});
        setStatusFilter("all");

        try {
            const data = await file.arrayBuffer();
            const wb = XLSX.read(data);
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

            if (!rows.length) {
                setMessage({ type: "error", text: "The uploaded file contains no data rows.", source: "import" });
                setImporting(false);
                return;
            }

            const requiredCols = ["Product ID", "Product Name", "Description", "Zamp Tax Code"];
            const headerKeys = Object.keys(rows[0]);
            const missingCols = requiredCols.filter(c => !headerKeys.includes(c));
            if (missingCols.length > 0) {
                setMessage({ type: "error", text: `Missing required columns: ${missingCols.join(", ")}. Expected: ${requiredCols.join(", ")}`, source: "import" });
                setImporting(false);
                return;
            }

            const itemMap = new Map(items.map(it => [it.id, it]));
            const validTaxCodes = new Set(taxCodes.map(tc => tc.code));

            const errors = [];
            const newOrphanErrors = [];
            let successCount = 0;
            const updatedItems = [...items];
            const newStatuses = {};

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const rowNum = i + 2;
                const productId = String(row["Product ID"] || "").trim();
                const name = String(row["Product Name"] || "").trim();
                const description = String(row["Description"] || "").trim();
                const taxCode = String(row["Zamp Tax Code"] || "").trim();

                setImportProgress(Math.round(((i + 1) / rows.length) * 100));

                if (!productId) {
                    const err = { row: rowNum, itemCode: name || "(empty)", itemName: name, taxCode, error: "Product ID is empty" };
                    errors.push(err);
                    newOrphanErrors.push({ id: `orphan-${rowNum}`, name, description, zampTaxCode: taxCode, _orphan: true, _error: err.error });
                    continue;
                }

                const existingItem = itemMap.get(productId);
                if (!existingItem) {
                    const err = { row: rowNum, itemCode: name || productId, itemName: name, taxCode, error: "Product ID not found in the system" };
                    errors.push(err);
                    newOrphanErrors.push({ id: `orphan-${rowNum}`, name, description, zampTaxCode: taxCode, _orphan: true, _error: err.error });
                    continue;
                }

                if (name && existingItem.name && name.toLowerCase() !== existingItem.name.toLowerCase()) {
                    newStatuses[productId] = {
                        status: ROW_STATUS.ERROR,
                        tooltip: `Import error: Product Name mismatch. Expected "${existingItem.name}"`,
                        error: `Product Name mismatch. Expected: "${existingItem.name}", Got: "${name}"`,
                        rowData: { itemName: name, description, taxCode },
                    };
                    errors.push({ row: rowNum, itemCode: name, itemName: name, taxCode, error: newStatuses[productId].error });
                    continue;
                }

                if (taxCode && validTaxCodes.size > 0 && !validTaxCodes.has(taxCode)) {
                    newStatuses[productId] = {
                        status: ROW_STATUS.ERROR,
                        tooltip: `Import error: Invalid tax code "${taxCode}"`,
                        error: `Invalid Zamp Tax Code: "${taxCode}"`,
                        rowData: { itemName: name, description, taxCode },
                    };
                    errors.push({ row: rowNum, itemCode: name, itemName: name, taxCode, error: newStatuses[productId].error });
                    continue;
                }

                const idx = updatedItems.findIndex(it => it.id === productId);
                if (idx !== -1) {
                    const previousTaxCode = updatedItems[idx].zampTaxCode || "";
                    if (taxCode === previousTaxCode) {
                        // unchanged
                    } else {
                        updatedItems[idx] = { ...updatedItems[idx], zampTaxCode: taxCode };
                        newStatuses[productId] = { status: ROW_STATUS.IMPORTED, tooltip: "Updated via import — not yet saved" };
                        successCount++;
                    }
                }

                if ((i + 1) % 100 === 0) {
                    await new Promise(r => setTimeout(r, 0));
                }
            }

            setItems(updatedItems);
            setRowStatuses(newStatuses);
            setOrphanErrors(newOrphanErrors);
            if (successCount > 0) {
                setUnsavedChanges(true);
            }

            setImportProgress(100);
            setImportSummary({
                total: rows.length,
                success: successCount,
                failed: errors.length,
                errors,
            });

            if (errors.length === 0) {
                setMessage({ type: "success", text: `Import completed. ${successCount} mappings updated.`, source: "import" });
            } else if (successCount > 0) {
                setMessage({ type: "warning", text: `Import completed with warnings. ${successCount} updated, ${errors.length} skipped.`, source: "import" });
            } else {
                setMessage({ type: "error", text: `Import failed. All ${errors.length} rows had errors.`, source: "import" });
            }
        } catch (err) {
            setMessage({ type: "error", text: `Failed to read file: ${err.message}`, source: "import" });
        } finally {
            setImporting(false);
        }
    };

    const hasActiveStatuses = Object.keys(rowStatuses).length > 0;

    const filteredItems = (() => {
        const search = filterText.toLowerCase();
        const base = items.filter(it => {
            const matchesText = (it.name || "").toLowerCase().includes(search) ||
                (it.description || "").toLowerCase().includes(search) ||
                (it.zampTaxCode || "").toLowerCase().includes(search);
            if (!matchesText) return false;
            if (statusFilter === "all") return true;
            const s = rowStatuses[it.id]?.status || ROW_STATUS.SAVED;
            return s === statusFilter;
        });
        if (statusFilter === ROW_STATUS.ERROR && orphanErrors.length > 0) {
            const filtered = orphanErrors.filter(o =>
                (o.name || "").toLowerCase().includes(search) ||
                (o.description || "").toLowerCase().includes(search) ||
                (o.zampTaxCode || "").toLowerCase().includes(search)
            );
            return [...base, ...filtered];
        }
        return base;
    })();

    const statusCounts = (() => {
        if (!hasActiveStatuses) return {};
        const counts = { [ROW_STATUS.SAVED]: 0, [ROW_STATUS.MODIFIED]: 0, [ROW_STATUS.IMPORTED]: 0, [ROW_STATUS.ERROR]: 0 };
        items.forEach(it => {
            const s = rowStatuses[it.id]?.status || ROW_STATUS.SAVED;
            counts[s]++;
        });
        return counts;
    })();

    const getRowStatusIcon = (item) => {
        const info = rowStatuses[item.id];
        if (!info) {
            return (
                <Tooltip title="Saved" placement="left">
                    <CloudDoneOutlinedIcon sx={{ fontSize: 16, color: "grey.400" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.MODIFIED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <EditIcon sx={{ fontSize: 16, color: "warning.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.IMPORTED) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                </Tooltip>
            );
        }
        if (info.status === ROW_STATUS.ERROR) {
            return (
                <Tooltip title={info.tooltip} placement="left">
                    <ErrorOutlineIcon
                        sx={{ fontSize: 16, color: "error.main", cursor: "pointer" }}
                        onClick={() => setErrorDetail(info)}
                    />
                </Tooltip>
            );
        }
        return null;
    };

    if (loading) return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 64px)" }}>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading mappings...
            </Typography>
        </Box>
    );

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Typography className="zamp-page-title" sx={{ mb: 2, textAlign: "center" }}>
                Product Tax Code Mapping Utility
            </Typography>

            <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 1050, width: "100%", mx: "auto", mt: 1 }}>
                <Stack spacing={2} direction="column" sx={{ width: "100%", mt: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
                        <TextField
                            size="small"
                            label="Filter by name, description, or tax code"
                            placeholder="Start typing to filter"
                            value={filterText}
                            onChange={e => {
                                setFilterText(e.target.value);
                                setPage(0);
                                if (message.text) setMessage({ type: "", text: "", source: "" });
                            }}
                            sx={{ flexBasis: "50%" }}
                        />
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                ref={fileInputRef}
                                onChange={handleImportFile}
                                style={{ display: "none" }}
                            />
                            <Tooltip title="Import mappings from Excel file">
                                <span>
                                    <Button variant="outlined" size="small" startIcon={<FileUploadIcon />} onClick={handleImportClick} disabled={importing || items.length === 0}>
                                        Import
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Export mappings to Excel file">
                                <span>
                                    <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={items.length === 0}>
                                        Export
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Refresh products">
                                <IconButton
                                    type="button"
                                    className="zamp-icon-action"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                >
                                    <RefreshIcon sx={{ fontSize: 24, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {importing && (
                        <Box sx={{ width: "100%" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">Importing...</Typography>
                                <Typography variant="body2" color="text.secondary">{importProgress}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={importProgress} sx={{ height: 8, borderRadius: 4 }} />
                        </Box>
                    )}

                    {importSummary && !importing && (
                        <Alert
                            severity={importSummary.failed === 0 ? "success" : importSummary.success > 0 ? "warning" : "error"}
                            onClose={() => { setImportSummary(null); setOrphanErrors([]); setRowStatuses(prev => { const next = {}; Object.entries(prev).forEach(([k, v]) => { if (v.status !== ROW_STATUS.ERROR) next[k] = v; }); return next; }); setStatusFilter("all"); }}
                            sx={{ alignItems: "flex-start" }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                {importSummary.failed === 0 ? "Import completed" : "Import completed with warnings"}
                            </Typography>
                            <Typography variant="body2">
                                {importSummary.success} updated &nbsp;&bull;&nbsp; {importSummary.failed} skipped &nbsp;&bull;&nbsp; {importSummary.total} total
                            </Typography>
                            {importSummary.failed > 0 && (
                                <Button size="small" variant="text" sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: "none" }}
                                    onClick={() => setImportSummary({ ...importSummary, showErrors: true })}>
                                    View {importSummary.failed} error{importSummary.failed > 1 ? "s" : ""}
                                </Button>
                            )}
                        </Alert>
                    )}

                    {hasActiveStatuses && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
                            <Chip label={`All (${items.length})`} size="small" variant={statusFilter === "all" ? "filled" : "outlined"} onClick={() => { setStatusFilter("all"); setPage(0); }} />
                            {(statusCounts[ROW_STATUS.IMPORTED] || 0) > 0 && (
                                <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={statusCounts[ROW_STATUS.IMPORTED]} size="small"
                                    variant={statusFilter === ROW_STATUS.IMPORTED ? "filled" : "outlined"} color="success"
                                    onClick={() => { setStatusFilter(ROW_STATUS.IMPORTED); setPage(0); }} />
                            )}
                            {(statusCounts[ROW_STATUS.MODIFIED] || 0) > 0 && (
                                <Chip icon={<EditIcon sx={{ fontSize: 14 }} />} label={statusCounts[ROW_STATUS.MODIFIED]} size="small"
                                    variant={statusFilter === ROW_STATUS.MODIFIED ? "filled" : "outlined"} color="warning"
                                    onClick={() => { setStatusFilter(ROW_STATUS.MODIFIED); setPage(0); }} />
                            )}
                            {((importSummary?.failed || 0) > 0 || (statusCounts[ROW_STATUS.ERROR] || 0) > 0) && (
                                <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />} label={importSummary?.failed || statusCounts[ROW_STATUS.ERROR] || 0} size="small"
                                    variant={statusFilter === ROW_STATUS.ERROR ? "filled" : "outlined"} color="error"
                                    onClick={() => { setStatusFilter(ROW_STATUS.ERROR); setPage(0); }} />
                            )}
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
                    )}

                    <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "25%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Product Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "35%", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, width: "40%" }}>Zamp Tax Code</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => {
                                    const isOrphan = !!item._orphan;
                                    const info = isOrphan ? null : rowStatuses[item.id];
                                    const s = isOrphan ? ROW_STATUS.ERROR : info?.status;
                                    const rowBg = s === ROW_STATUS.IMPORTED
                                        ? (index % 2 === 0 ? "#f6faf3" : "#eef5ea")
                                        : s === ROW_STATUS.ERROR
                                            ? (index % 2 === 0 ? "#fef7f7" : "#fdf0f0")
                                            : s === ROW_STATUS.MODIFIED
                                                ? (index % 2 === 0 ? "#fffbf0" : "#fff7e6")
                                                : (index % 2 === 0 ? "white" : "grey.100");

                                    return (
                                        <TableRow key={item.id} sx={{ backgroundColor: rowBg }}>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 250, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                <Tooltip title={item.name || ""} placement="bottom"
                                                    slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, 8] } }] }, tooltip: { sx: { color: "white", fontSize: "0.8rem", borderRadius: 1, px: 1.5, py: 0.5 } } }}>
                                                    <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {item.name || "-"}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                <Tooltip title={item.description || ""} placement="bottom"
                                                    slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, 8] } }] }, tooltip: { sx: { color: "white", fontSize: "0.8rem", borderRadius: 1, px: 1.5, py: 0.5 } } }}>
                                                    <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {item.description || "-"}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        {isOrphan ? (
                                                            <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13, fontStyle: "italic", pl: 1 }}>
                                                                {item.zampTaxCode || "-"}
                                                            </Typography>
                                                        ) : (() => {
                                                            const isDeletedCode = item.zampTaxCode && !taxCodes.find(tc => tc.code === item.zampTaxCode);
                                                            const currentValue = taxCodes.find(tc => tc.code === item.zampTaxCode)
                                                                || (isDeletedCode ? { code: item.zampTaxCode } : null);
                                                            return (
                                                                <Autocomplete
                                                                    options={taxCodes}
                                                                    getOptionLabel={(option) => option?.code ?? ""}
                                                                    renderOption={(props, option) => <li {...props}>{option.code}</li>}
                                                                    value={currentValue}
                                                                    onChange={(e, newValue) => handleTaxCodeChange(item.id, newValue ? newValue.code : "")}
                                                                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} size="small"
                                                                            placeholder="Select tax code"
                                                                            error={!!isDeletedCode}
                                                                            helperText={isDeletedCode ? `"${item.zampTaxCode}" no longer exists` : undefined}
                                                                        />
                                                                    )}
                                                                    fullWidth
                                                                />
                                                            );
                                                        })()}
                                                    </Box>
                                                    <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center" }}>
                                                        {isOrphan ? (
                                                            <Tooltip title={item._error} placement="left">
                                                                <ErrorOutlineIcon
                                                                    sx={{ fontSize: 14, color: "error.main", cursor: "pointer" }}
                                                                    onClick={() => setErrorDetail({
                                                                        error: item._error,
                                                                        rowData: { itemName: item.name, description: item.description, taxCode: item.zampTaxCode },
                                                                    })}
                                                                />
                                                            </Tooltip>
                                                        ) : getRowStatusIcon(item)}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredItems.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />

                    <Stack spacing={1} sx={{ mt: 2, width: "100%", maxWidth: 800 }}>
                        {message.source === "save" && message.type === "error" && (
                            <Typography color="error" sx={{ fontSize: 14 }}>{message.text}</Typography>
                        )}
                        {message.source === "save" && message.type === "success" && (
                            <Typography color="success.main" sx={{ fontSize: 14 }}>{message.text}</Typography>
                        )}
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !unsavedChanges} sx={{ width: 250 }}
                            startIcon={saving ? <CircularProgress size={18} thickness={5} sx={{ color: "white" }} /> : null}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog open={!!importSummary?.showErrors} onClose={() => setImportSummary(prev => prev ? { ...prev, showErrors: false } : null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold" }}>Import Errors ({importSummary?.failed || 0} rows skipped)</DialogTitle>
                <DialogContent dividers>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", width: 80 }}>Row</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", width: 200 }}>Product Name</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Error</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {importSummary?.errors?.map((err, i) => (
                                    <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? "white" : "grey.50" }}>
                                        <TableCell>{err.row}</TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{err.itemCode}</TableCell>
                                        <TableCell sx={{ color: "error.main", fontSize: 13 }}>{err.error}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImportSummary(prev => prev ? { ...prev, showErrors: false } : null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!errorDetail} onClose={() => setErrorDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>Error Details</DialogTitle>
                <DialogContent dividers>
                    {errorDetail?.rowData && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Product Name</Typography>
                                <Typography variant="body1">{errorDetail.rowData.itemName || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                <Typography variant="body1">{errorDetail.rowData.description || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Zamp Tax Code</Typography>
                                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{errorDetail.rowData.taxCode || "-"}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Error</Typography>
                                <Typography variant="body1" color="error.main">{errorDetail.error}</Typography>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Backdrop open={saving} className="zamp-backdrop-loader" sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
                <CircularProgress size={48} thickness={4} color="inherit" />
                <Typography variant="body1">Saving mappings...</Typography>
            </Backdrop>
        </Box>
    );
}

export default TaxCodeMapping;
